import {Action, Reducer} from "redux";
import axios, {AxiosError} from "axios";
import {
  NavigationActionTypes,
  REQUEST_NAVIGATION,
  RECEIVE_NAVIGATION,
  RELOAD_NAVIGATION,
} from "../actions/NavigationActionTypes";
import {NavigationHierarchyDto} from "../types/NavigationHierarchyDto";
import {CommonAppThunkAction} from ".";
import {getFoundationServiceSchemeAndHost} from "../utils/getFoundationServiceSchemeAndHost";
import {ReceivedError, receiveAxiosError} from "../features/errors/data/errorSlice";
import {PayloadAction} from "@reduxjs/toolkit";

// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface NavigationState {
  isLoadingNavigation: boolean;
  isLoadedNavigation: boolean;
  navigation: NavigationHierarchyDto;
}

// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

export const actionCreators = {
  requestNavigation:
    (): CommonAppThunkAction<NavigationActionTypes | PayloadAction<ReceivedError>> => (dispatch, getState) => {
      // Only load data if it's something we don't already have (and are not already loading)
      const appState = getState();

      if (
        appState &&
        appState.navigation !== undefined &&
        !appState.navigation.isLoadingNavigation &&
        !appState.navigation.isLoadedNavigation
      ) {
        dispatch({type: REQUEST_NAVIGATION});

        const url = `${getFoundationServiceSchemeAndHost()}/api/nav`;

        axios
          .request<NavigationHierarchyDto>({
            url: url,
          })
          .then((response) => {
            const {data} = response;
            dispatch({type: RECEIVE_NAVIGATION, navigation: data});
          })
          .catch((e: AxiosError) => {
            dispatch(receiveAxiosError({error: e, message: "Failed to retrieve navigation menu data from API."}));
          });
      }
    },

  reloadNavigation: (): CommonAppThunkAction<NavigationActionTypes> => (dispatch) => {
    dispatch({type: RELOAD_NAVIGATION});
  },
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.

export const reducer: Reducer<NavigationState> = (
  state: NavigationState | undefined,
  incomingAction: Action
): NavigationState => {
  if (state === undefined) {
    return {
      isLoadingNavigation: false,
      isLoadedNavigation: false,
      navigation: {
        service: "",
        basePath: "",
        nav: [],
      },
    };
  }

  const action = incomingAction as NavigationActionTypes;
  switch (action.type) {
    case REQUEST_NAVIGATION:
      return {
        ...state,
        isLoadingNavigation: true,
        isLoadedNavigation: false,
      };
    case RECEIVE_NAVIGATION:
      return {
        ...state,
        isLoadingNavigation: false,
        isLoadedNavigation: true,
        navigation: action.navigation,
      };
    case RELOAD_NAVIGATION:
      return {
        ...state,
        isLoadingNavigation: false,
        isLoadedNavigation: false,
      };
  }

  return state;
};
